// THE STOCK DETAIL PANEL. READ-ONLY, AND QUANTITY-ONLY.
//
// Read-only in the strict sense: a single Close, no Save. This app holds a
// READ-ONLY PROJECTION of stock. Adjusting a level is a write to the system of
// record with a reason code and an audit trail behind it, and it belongs in
// Odoo or the WMS -- which is why the Stock Adjustment button went with this
// slice rather than being disabled.
//
// ⚠️ THE MONEY BLOCK AND THE ALLOCATION BLOCK ARE GONE, PERMANENTLY. The seam
// reports product_id, location, quantity, as_of and source. There is no cost,
// no value, and no reservation data anywhere behind it -- see
// addons/anixify_cpq/models/stock.py, and the grep test that pins the absence
// from both sides.
//
// What replaces them is the thing that IS real: the per-location breakdown the
// endpoint returns for ?locations=1, with each level's own as_of and source.

import type { InventoryRow } from "@/services/inventoryRows";
import { semanticColors, colors, radius, spacing, heights, typography } from "@/styles/tokens";
import { DetailPanel } from "@/components/DetailPanel";
import { formatDate } from "@/lib/format";

const LABEL: React.CSSProperties = { fontSize: typography.fontSize.sm, color: semanticColors.textSecondary };
const VALUE: React.CSSProperties = { fontSize: typography.fontSize.sm, color: semanticColors.textPrimary, fontVariantNumeric: "tabular-nums" };

function KVRow({
  label, value, testid, strong,
}: { label: string; value: React.ReactNode; testid?: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between" data-testid={testid}>
      <span style={LABEL}>{label}</span>
      <span style={{ ...VALUE, fontWeight: strong ? typography.fontWeight.semibold : typography.fontWeight.regular }}>{value}</span>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: typography.fontSize.xs, color: semanticColors.textMuted,
      textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide,
      fontWeight: typography.fontWeight.medium, marginBottom: spacing[8],
    }}>
      {children}
    </div>
  );
}

export function InventoryDetailPanel({
  row,
  levels,
  onClose,
}: {
  row: InventoryRow | null;
  /** Every level reported for THIS product, so the panel can show the
   *  breakdown behind the row the user clicked. Empty when the product was
   *  never reported on -- which the panel says out loud rather than showing
   *  an empty list that reads as "no stock". */
  levels: InventoryRow[];
  onClose: () => void;
}) {
  if (!row) return null;

  const product = row.product;
  const anyReported = levels.some((l) => l.qtyOnHand !== undefined);

  return (
    <DetailPanel
      testIdPrefix="inventory"
      title={product.partNo}
      subtitle={product.description}
      onClose={onClose}
      footer={
        <button
          data-testid="inventory-panel-close-btn"
          className="btn-secondary"
          onClick={onClose}
          style={{
            height: heights.control, padding: `0 ${spacing[14]}px`,
            backgroundColor: semanticColors.surface, color: semanticColors.textSecondary,
            border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
            fontSize: typography.fontSize.base, cursor: "pointer",
          }}
        >
          Close
        </button>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: spacing[6] }}>
        <KVRow label="Part No." value={product.partNo} testid="inv-partNo" />
        <KVRow label="Description" value={product.description} testid="inv-description" />
        {/* ⚠️ A DASH FOR NULL. Unclassified is a real state on the real
            master, and naming a brand nobody assigned would be inventing one. */}
        <KVRow label="Brand" value={product.brand ?? "—"} testid="inv-brand" />
        <KVRow label="Category" value={product.category ?? "—"} testid="inv-category" />
        {product.manufacturer ? (
          <KVRow label="Manufacturer" value={product.manufacturer} testid="inv-manufacturer" />
        ) : null}
      </div>

      <div style={{ marginTop: spacing[14], paddingTop: spacing[14], borderTop: `1px solid ${semanticColors.border}` }}>
        <SectionLabel>Reported on hand</SectionLabel>

        {/* ⚠️ NOT AVAILABLE-TO-PROMISE, AND THE PANEL SAYS SO. "Available"
            means on hand minus what is already spoken for, and this system
            holds no allocation data at all -- so a figure labelled that way
            would be promised against. */}
        <div
          data-testid="inventory-panel-atp-note"
          style={{
            fontSize: typography.fontSize.xs, color: semanticColors.textMuted,
            marginBottom: spacing[10], lineHeight: typography.lineHeight.base,
          }}
        >
          Quantities are what a source reported as on hand. No allocations or
          commitments are known to this system, so these are not
          available-to-promise figures.
        </div>

        {!anyReported ? (
          // ⚠️ SAID, NOT SHOWN AS ZERO. An empty list here would read as "no
          // stock"; the fact is that nobody has reported on this product.
          <div
            data-testid="inventory-panel-unreported"
            style={{ fontSize: typography.fontSize.sm, color: semanticColors.textPlaceholder }}
          >
            Not reported — no source has told this system anything about this product.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: spacing[6] }}>
            {levels.map((level, i) => (
              <div
                key={level.key}
                data-testid={`inventory-panel-level-${i}`}
                style={{
                  padding: `${spacing[8]}px ${spacing[10]}px`,
                  backgroundColor: semanticColors.panelBg,
                  border: `1px solid ${semanticColors.border}`,
                  borderRadius: radius.base,
                }}
              >
                <div className="flex items-center justify-between">
                  <span style={{ fontSize: typography.fontSize.sm, color: semanticColors.textPrimary }}>
                    {/* Null means "not attributed to a location", which is what
                        a company-wide figure is. Not "ALL". */}
                    {level.location ?? "Not stated"}
                  </span>
                  <span style={{
                    ...VALUE,
                    fontWeight: typography.fontWeight.semibold,
                    color: level.qtyOnHand === 0 ? colors.red600 : semanticColors.textPrimary,
                  }}>
                    {level.qtyOnHand}
                  </span>
                </div>
                <div style={{ fontSize: typography.fontSize.xs, color: semanticColors.textMuted, marginTop: spacing[2] }}>
                  {/* ⚠️ DASHED WHEN NULL, NEVER STAMPED WITH "NOW". Nothing
                      populates as_of today -- the mock adapter deliberately
                      leaves it unset, because dating a fixture would dress it
                      up as a fresh reading. It is here, beside the level it
                      belongs to, rather than as a table column that would be
                      empty on every row and teach people to ignore it. */}
                  As of {level.asOf ? formatDate(level.asOf) : "—"}
                  {level.source ? ` · ${level.source}` : ""}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DetailPanel>
  );
}
